/* eslint-disable fp/no-mutating-methods */
import { Decimal } from 'decimal.js-light'

import { randomIntFromChoices, randomIntFromInterval } from '~/bootcamp/Activities/utils/activities.utils'

import { fromEntries, range } from '~/utils/helpers'

import { selectRandomItem } from '../Negative/helper'

import { Cell, LatticeRow, LatticeRowType, LongMultiDecimalGeneratedValues } from './types'

export const generateNumber = (minDigit: number, maxDigit: number, len: number, excludeArr: number[]): number => {
  const numArr = range(len).map(() => randomIntFromInterval(minDigit, maxDigit))
  const num = parseInt(numArr.join(''), 10)

  if (excludeArr.includes(num)) {
    return generateNumber(minDigit, maxDigit, len, excludeArr)
  } else {
    return num
  }
}

export function multiplyDecimals(num1: number, num2: number, toPrecision?: number): number | string {
  const n = new Decimal(num1)
  if (toPrecision) {
    return n.mul(num2).toPrecision(toPrecision)
  }
  return n.mul(num2).toNumber()
}

export function compareDecimals(num1: number | string, num2: number): boolean {
  const n = new Decimal(num1)
  return n.equals(num2)
}

export function generateTerms(
  specOne: {
    multiplicandDigitMin: number
    multiplicandDigitMax: number
    multiplicandLen: number
    taskHistoryMultiplicands: number[]
  },
  specTwo: {
    multiplierDigitMin: number
    multiplierDigitMax: number
    multiplierLen: number
    taskHistoryMultipliers: number[]
  },
  diffiClultyLevel: number,
): LongMultiDecimalGeneratedValues {
  const { multiplicandDigitMin, multiplicandDigitMax, multiplicandLen, taskHistoryMultiplicands } = specOne
  const { multiplierDigitMin, multiplierDigitMax, multiplierLen, taskHistoryMultipliers } = specTwo

  const numberOne = generateNumber(
    multiplicandDigitMin,
    multiplicandDigitMax,
    multiplicandLen,
    taskHistoryMultiplicands,
  )
  const numberTwo = generateNumber(multiplierDigitMin, multiplierDigitMax, multiplierLen, taskHistoryMultipliers)

  // @todo pass object
  const numberArr = [numberOne, numberTwo]
  const { val, index } = selectRandomItem(numberArr)
  const result: LongMultiDecimalGeneratedValues = {} as LongMultiDecimalGeneratedValues

  const valLength = val.toString().length
  const probability = 100 / valLength

  switch (diffiClultyLevel) {
    case 1: {
      const probableNum = probabilitySelection(val, probability, 10)

      if (index) {
        result['multiplier'] = probableNum
        result['multiplicand'] = generateDecimal(numberArr[0], 10)
      } else {
        result['multiplier'] = generateDecimal(numberArr[1], 10)
        result['multiplicand'] = probableNum
      }

      return result
    }

    case 2: {
      // length 3,2
      const probableDivisor = valLength === 3 ? selectRandomItem([10, 100]).val : 10
      const probableNum = probabilitySelection(val, probability, probableDivisor)

      if (index) {
        result['multiplier'] = probableNum
        const divisor = selectRandomItem([10, 100]).val
        result['multiplicand'] = generateDecimal(numberArr[0], divisor)
      } else {
        result['multiplier'] = generateDecimal(numberArr[1], 10)
        result['multiplicand'] = probableNum
      }

      return result
    }

    case 3: {
      const divisor = selectRandomItem([10, 100]).val
      const probableNum = probabilitySelection(val, probability, divisor)

      if (index) {
        result['multiplier'] = probableNum
        result['multiplicand'] = generateDecimal(numberArr[0], divisor)
      } else {
        result['multiplier'] = generateDecimal(numberArr[1], divisor)
        result['multiplicand'] = probableNum
      }
      return result
    }

    case 4: {
      // length 3,2
      const probableDivisor = valLength === 4 ? selectRandomItem([10, 100, 1000]).val : selectRandomItem([10, 100]).val
      const probableNum = probabilitySelection(val, probability, probableDivisor)

      if (index) {
        result['multiplier'] = probableNum
        const divisor = selectRandomItem([10, 100, 100]).val
        result['multiplicand'] = generateDecimal(numberArr[0], divisor)
      } else {
        const divisor = selectRandomItem([10, 100]).val
        result['multiplier'] = generateDecimal(numberArr[1], divisor)
        result['multiplicand'] = probableNum
      }
      return result
    }

    default:
      throw new Error('Out of range')
  }
}

// @todo improve distribution
export function probabilitySelection(num: number, probability: number, divisor: number): number {
  const distributionLength = Math.floor((probability / 100) * 10)
  const decimalNum = generateDecimal(num, divisor)
  const distribution = new Array(10).fill(num, 0, distributionLength).fill(decimalNum, distributionLength)

  const { val: selectedValue } = selectRandomItem(distribution)

  return selectedValue
}

function generateDecimal(num: number, divisor: number): number {
  const digit = new Decimal(num)
  if (num % divisor !== 0) {
    return digit.dividedBy(divisor).toNumber()
  }
  return generateDecimal(num + 1, divisor)
}

const latticeIntegerRanges = [
  { multiplier: { min: 10, max: 99 }, multiplicand: { min: 2, max: 9 } },
  { multiplier: { min: 100, max: 999 }, multiplicand: { min: 2, max: 9 } },
  { multiplier: { min: 11, max: 99 }, multiplicand: { min: 10, max: 99 } },
  { multiplier: { min: 100, max: 999 }, multiplicand: { min: 10, max: 99 } },
  { multiplier: { min: 100, max: 999 }, multiplicand: { min: 100, max: 999 } },
  { multiplier: { min: 1000, max: 9999 }, multiplicand: { min: 100, max: 999 } },
]

export function generateLattice(level: number): {
  latticeArray: LatticeRow[]
  gridColumns: number
  gridRows: number
  multiplier: string
  multiplicand: string
} {
  const { multiplicand: multiplicandRange, multiplier: multiplierRange } = latticeIntegerRanges[level - 1]
  const multiplier = String(randomIntFromInterval(multiplierRange.min, multiplierRange.max))
  const multiplicand = String(randomIntFromInterval(multiplicandRange.min, multiplicandRange.max))
  const latticeArray: LatticeRow[] = []

  const multiplierArray = Array.from(multiplier)
  const multiplicandArray = Array.from(multiplicand)

  // first number row
  multiplierArray.forEach((item, index) => {
    latticeArray.push({ type: LatticeRowType.NUM, value: item, itemIndex: index + 1 })
  })

  // carried
  multiplierArray.forEach((num, index) => {
    latticeArray.push({ type: LatticeRowType.CARR, name: `carried:${index + 1}` })
  })

  // lattice grid
  multiplicandArray
    .map((digit1, plicandIndex) => {
      let fieldCoord = 1 + plicandIndex
      const sumRowName = `sum:${fieldCoord}`
      const mutliplicandCoord = multiplier.length + plicandIndex + 1
      const hasCarriedMultiplicand = multiplier.length + fieldCoord <= multiplier.length + multiplicand.length - 2

      return [
        { type: LatticeRowType.SUM, name: sumRowName, itemIndex: plicandIndex + 1 },
        ...multiplierArray.map((_, plierIndex) => {
          const multiplierCoord = plierIndex + 1
          const position = `${multiplierCoord}-${mutliplicandCoord}`

          const cell1Name = `${fieldCoord}:${position}`
          fieldCoord += 1
          const cell2Name = `${fieldCoord}:${position}`

          return { type: LatticeRowType.CELL, names: [cell1Name, cell2Name], name: cell1Name + cell2Name }
        }),
        {
          type: LatticeRowType.NUM,
          value: digit1,
          hasCarriedNumber: hasCarriedMultiplicand,
          carriedName: `carried:${fieldCoord}`,
        },
      ]
    })
    .forEach((item) => {
      latticeArray.push(...item)
    })

  // footer row
  multiplierArray.forEach((_, index) => {
    const iIndex: number = multiplicandArray.length + index + 1
    const name = `sum:${iIndex}`
    latticeArray.push({ type: LatticeRowType.SUM, name, itemIndex: iIndex })
  })

  return { latticeArray, gridColumns: multiplier.length, gridRows: multiplicand.length, multiplicand, multiplier }
}

const latticeDecimalRanges = [
  {
    multiplier: { min: 100, max: 999, divisor: [10, 100] },
    multiplicand: { min: 2, max: 9 },
  },
  {
    multiplier: { min: 10, max: 99, divisor: [10] },
    multiplicand: { min: 10, max: 99, divisor: [10] },
  },
  {
    multiplier: { min: 100, max: 999, divisor: [10, 100] },
    multiplicand: { min: 10, max: 99, divisor: [10] },
  },
  {
    multiplier: { min: 100, max: 999, divisor: [10, 100] },
    multiplicand: { min: 100, max: 999, divisor: [10, 100] },
  },
  {
    multiplier: { min: 1000, max: 9999, divisor: [10, 100, 1000] },
    multiplicand: { min: 100, max: 999, divisor: [10, 100] },
  },
]

const defaultLatticeValues = {
  latticeArray: [] as LatticeRow[],
  gridColumns: 0,
  gridRows: 0,
  multiplicand: '0',
  multiplier: '0',
}

export function generateLatticeDecimal(level: number): {
  latticeArray: LatticeRow[]
  gridColumns: number
  gridRows: number
  multiplier: string | number
  multiplicand: string | number
} {
  if (!level || level > latticeDecimalRanges.length) return defaultLatticeValues

  const { multiplicand: multiplicandRange, multiplier: multiplierRange } = latticeDecimalRanges[level - 1]
  const multiplierNum = randomIntFromInterval(multiplierRange.min, multiplierRange.max)
  const multiplicandNum = randomIntFromInterval(multiplicandRange.min, multiplicandRange.max)

  const multiplier = generateDecimal(multiplierNum, randomIntFromChoices(multiplierRange.divisor))
  const multiplicand = multiplicandRange.divisor
    ? generateDecimal(multiplicandNum, randomIntFromChoices(multiplicandRange.divisor))
    : multiplicandNum

  const latticeArray: LatticeRow[] = []

  const multiplierArray = Array.from(String(multiplier)).filter((val) => val !== '.')
  const multiplicandArray = Array.from(String(multiplicand)).filter((val) => val !== '.')

  const multiplierNumLength = multiplierArray.length
  const multiplicandNumLength = multiplicandArray.length

  if (String(multiplyDecimals(multiplier, multiplicand)).length - 1 < multiplierNumLength + multiplicandNumLength) {
    return generateLatticeDecimal(level)
  }

  // first number row
  multiplierArray.forEach((item, itemIndex) => {
    latticeArray.push({ type: LatticeRowType.NUM, value: item, decimalPosition: 'right', itemIndex: itemIndex + 1 })
  })

  // carried
  multiplierArray.forEach((num, index) => {
    latticeArray.push({ type: LatticeRowType.CARR, name: `carried:${index + 1}` })
  })

  // lattice grid
  multiplicandArray
    .map((digit1, i) => {
      // 1 iterate over row
      const plicandIndex = i + 1
      let fieldCoord = plicandIndex
      const sumRowName = `sum:${fieldCoord}`
      const mutliplicandCoord = multiplierArray.length + plicandIndex
      const sumDecimalPosition = multiplicandNumLength === plicandIndex ? undefined : 'bottom'
      const hasCarriedMultiplicand =
        multiplierNumLength + plicandIndex <= multiplierNumLength + multiplicandNumLength - 2

      return [
        { type: LatticeRowType.SUM, name: sumRowName, decimalPosition: sumDecimalPosition, itemIndex: plicandIndex },
        // 2 iterate over columns
        ...multiplierArray.map((_, plierIndex) => {
          const multiplierCoord = plierIndex + 1
          const position = `${multiplierCoord}-${mutliplicandCoord}`

          const cell1Name = `${fieldCoord}:${position}`
          fieldCoord += 1
          const cell2Name = `${fieldCoord}:${position}`

          return { type: LatticeRowType.CELL, names: [cell1Name, cell2Name], name: cell1Name + cell2Name }
        }),
        {
          type: LatticeRowType.NUM,
          value: digit1,
          decimalPosition: 'bottom',
          itemIndex: plicandIndex,
          hasCarriedNumber: hasCarriedMultiplicand,
          carriedName: `carried:${fieldCoord}`,
        },
      ]
    })
    .forEach((item) => {
      latticeArray.push(...item)
    })

  // footer row
  multiplierArray.forEach((_, index) => {
    const iIndex: number = multiplicandNumLength + index + 1
    const name = `sum:${iIndex}`
    const decimalPosition = multiplierNumLength === index + 1 ? undefined : 'right'
    latticeArray.push({ type: LatticeRowType.SUM, name, decimalPosition, itemIndex: iIndex })
  })

  return {
    latticeArray,
    gridColumns: multiplierNumLength,
    gridRows: multiplicandNumLength,
    multiplicand,
    multiplier,
  }
}

export function getCellDefaultValues(objectLen: number): Record<string, Cell> {
  const val: Array<[number, Cell]> = [...new Array(objectLen)].map((_, index) => [index + 1, {}])
  return fromEntries(val)
}

export function getSumCellDefaultValues(latticeArray: LatticeRow[]): Record<string, string> {
  return latticeArray.reduce((prev, curr) => {
    if (curr.type === LatticeRowType.SUM) {
      return { ...prev, [curr.name]: '' }
    }
    return prev
  }, {})
}

export function generateLatticeGridTemplate(rowWidth: number, ColumnWidth: number): string {
  const xCells = new Array(rowWidth).fill('.').join('  ')
  const yCells = [...new Array(ColumnWidth)].map(() => `".  ${xCells}   ."`).join('\n')

  return `"space  ${xCells}   times"
  "space  ${xCells}   carriedSpace"
  ${yCells}
  "space2  ${xCells}   ."`
}

/* Judges a decimal long-multiplication answer in two parts, because the two ways of getting it
   wrong are different mistakes. Digits that spell out the right answer with the sign one column
   off is a place-value slip; digits that spell out something else is an arithmetic one. Telling
   a pupil which of the two they made is the whole point of a practice activity, so instead of
   comparing against one expected position we ask whether ANY position would have worked. That
   also copes with trailing zeros, where more than one written form is numerically right. */
export function evaluateDecimalAnswer({
  digits,
  decimalIndex,
  trueAnswer,
}: {
  digits: string[]
  decimalIndex?: number
  trueAnswer: number | string
}): { isCorrect: boolean; isDecimalMisplaced: boolean } {
  const answerWithDecimalAt = (position: number): number =>
    Number([...digits.slice(0, position), '.', ...digits.slice(position)].join(''))

  const matches = (position: number): boolean => {
    const candidate = answerWithDecimalAt(position)
    return Number.isFinite(candidate) && compareDecimals(trueAnswer, candidate)
  }

  const isCorrect = decimalIndex !== undefined && matches(decimalIndex)
  const isDecimalMisplaced =
    decimalIndex !== undefined && !isCorrect && range(digits.length - 1).some((index) => matches(index + 1))

  return { isCorrect, isDecimalMisplaced }
}
